// src/pages/volunteering/createForm.js
import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card, Divider, Dropdown, Input } from "antd";
import Layout from "../../components/layout/layout";
import ConfirmModal from "../../components/confirmModal/confirmModal";
import { createForm } from "./api/forms";
import FieldEditor from "./fieldEditor";
import FieldList from "./fieldList";
import FormPreview from "./formPreview";
import "./volunteering.css";

const { TextArea } = Input;

// ─── Reducer ─────────────────────────────────────────────────────────────────
const initialState = {
  formTitle: "",
  formDescription: "",
  externalFormUrl: "",
  eventDate: "",
  eventVenue: "",
  eventTime: "",
  fields: [],
  editorOpen: false,
  editFieldIndex: null,
  saveIntent: null, // "form" | "template" | "both"
  templateName: "",
  isSaving: false,
  showConfirmModal: false,
  notification: { show: false, message: "", type: "success" }
};

const reducer = (state, action) => {
  switch (action.type) {
    case "setField":
      return { ...state, [action.key]: action.value };
    case "setFields":
      return { ...state, fields: action.payload };
    case "setEditorOpen":
      return { ...state, editorOpen: action.payload };
    case "setEditFieldIndex":
      return { ...state, editFieldIndex: action.payload };
    case "setIsSaving":
      return { ...state, isSaving: action.payload };
    case "setConfirmModal":
      return { ...state, showConfirmModal: action.payload };
    case "setNotification":
      return { ...state, notification: action.payload };
    case "loadTemplate":
      return {
        ...state,
        formTitle: action.payload.title || "",
        formDescription: action.payload.description || "",
        fields: action.payload.formSchema?.fields || []
      };
    case "reset":
      return { ...initialState };
    default:
      return state;
  }
};

// ─── Component ───────────────────────────────────────────────────────────────
function CreateForm() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const location = useLocation();
  const navigate = useNavigate();

  const formType = location.state?.formType || "internal";
  const template = location.state?.template || null;

  const {
    formTitle,
    formDescription,
    externalFormUrl,
    eventDate,
    eventVenue,
    eventTime,
    fields,
    editorOpen,
    editFieldIndex,
    templateName,
    isSaving,
    showConfirmModal,
    notification
  } = state;

  // ── Notification helpers ──────────────────────────────────────────────────
  const showNotification = useCallback((message, type = "success") => {
    dispatch({
      type: "setNotification",
      payload: { show: false, message, type }
    });
    setTimeout(() => {
      dispatch({
        type: "setNotification",
        payload: { show: true, message, type }
      });
    }, 0);
  }, []);

  const hideNotification = useCallback(() => {
    dispatch({
      type: "setNotification",
      payload: { ...state.notification, show: false }
    });
  }, [state.notification]);

  // ── Load template ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (template) {
      dispatch({ type: "loadTemplate", payload: template });
    }
  }, [template]);

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = useCallback(() => {
    if (!formTitle.trim()) {
      showNotification("Form title is required", "error");
      return false;
    }
    if (!eventDate || !eventVenue || !eventTime) {
      showNotification("Event date, venue, and time are all required", "error");
      return false;
    }
    if (formType === "internal" && fields.length === 0) {
      showNotification("Please add at least one field", "error");
      return false;
    }
    if (formType === "external" && !externalFormUrl.trim()) {
      showNotification("Please provide an external form URL", "error");
      return false;
    }
    return true;
  }, [formTitle, eventDate, eventVenue, eventTime, formType, fields, externalFormUrl, showNotification]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSaveForm = useCallback(async () => {
    if (!validate()) return;
    dispatch({ type: "setIsSaving", payload: true });

    const intent = state.saveIntent;
    const savingAsForm = intent === "form" || intent === "both";
    const savingAsTemplate = intent === "template" || intent === "both";

    try {
      if (savingAsForm) {
        await createForm({
          formId: `form_${Date.now()}`,
          title: formTitle,
          description: formDescription,
          formType,
          eventDate,
          eventVenue,
          eventTime,
          formSchema: { fields: formType === "internal" ? fields : [] },
          externalFormUrl: formType === "external" ? externalFormUrl : null,
          isTemplate: false,
          templateName: null
        });
      }

      if (savingAsTemplate) {
        await createForm({
          formId: `template_${Date.now()}`,
          title: formTitle,
          description: formDescription,
          formType,
          eventDate,
          eventVenue,
          eventTime,
          formSchema: { fields: formType === "internal" ? fields : [] },
          externalFormUrl: formType === "external" ? externalFormUrl : null,
          isTemplate: true,
          templateName: state.templateName || formTitle
        });
      }

      const successMsg =
        intent === "both"
          ? "Saved as form and template successfully"
          : intent === "template"
            ? "Template saved successfully"
            : "Form saved successfully";

      showNotification(successMsg, "success");
      setTimeout(() => navigate("/volunteering/formList"), 1500);
    } catch (err) {
      console.error(err);
      showNotification("Failed to save", "error");
    } finally {
      dispatch({ type: "setIsSaving", payload: false });
      dispatch({ type: "setConfirmModal", payload: false });
    }
  }, [
    validate,
    state.saveIntent,
    state.templateName,
    formTitle,
    formDescription,
    formType,
    eventDate,
    eventVenue,
    eventTime,
    fields,
    externalFormUrl,
    showNotification,
    navigate
  ]);

  // ── Save menu items ───────────────────────────────────────────────────────
  const saveMenuItems = useMemo(
    () => ({
      items: [
        {
          key: "form",
          label: "Save as Form",
          onClick: () => {
            dispatch({ type: "setField", key: "saveIntent", value: "form" });
            dispatch({ type: "setConfirmModal", payload: true });
          }
        },
        {
          key: "template",
          label: "Save as Template",
          onClick: () => {
            dispatch({ type: "setField", key: "saveIntent", value: "template" });
            dispatch({ type: "setConfirmModal", payload: true });
          }
        },
        {
          key: "both",
          label: "Save as Form and Template",
          onClick: () => {
            dispatch({ type: "setField", key: "saveIntent", value: "both" });
            dispatch({ type: "setConfirmModal", payload: true });
          }
        }
      ]
    }),
    []
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Layout
      navTitle="Volunteer Forms"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="vfl-form-page">
        <h2 className="vfl-form-page-title">{template ? "Create From Template" : "Create Volunteer Form"}</h2>

        {/* ── Form Details ── */}
        <Card className="vfl-form-card">
          <div className="vfl-form-fields">
            <div className="vfl-form-group">
              <label className="vfl-form-label">Form Title *</label>
              <Input
                placeholder="Enter form title"
                value={formTitle}
                onChange={(e) =>
                  dispatch({
                    type: "setField",
                    key: "formTitle",
                    value: e.target.value
                  })
                }
              />
            </div>

            <div className="vfl-form-group">
              <label className="vfl-form-label">Description</label>
              <TextArea
                placeholder="Enter description"
                rows={3}
                value={formDescription}
                onChange={(e) =>
                  dispatch({
                    type: "setField",
                    key: "formDescription",
                    value: e.target.value
                  })
                }
              />
            </div>

            <div className="vfl-form-row">
              <div className="vfl-form-group">
                <label className="vfl-form-label">Event Date *</label>
                <Input
                  type="date"
                  value={eventDate}
                  onChange={(e) =>
                    dispatch({
                      type: "setField",
                      key: "eventDate",
                      value: e.target.value
                    })
                  }
                />
              </div>
              <div className="vfl-form-group">
                <label className="vfl-form-label">Event Time *</label>
                <Input
                  type="time"
                  value={eventTime}
                  onChange={(e) =>
                    dispatch({
                      type: "setField",
                      key: "eventTime",
                      value: e.target.value
                    })
                  }
                />
              </div>
            </div>

            <div className="vfl-form-group">
              <label className="vfl-form-label">Event Venue *</label>
              <Input
                placeholder="Enter event venue"
                value={eventVenue}
                onChange={(e) =>
                  dispatch({
                    type: "setField",
                    key: "eventVenue",
                    value: e.target.value
                  })
                }
              />
            </div>

            {formType === "external" && (
              <div className="vfl-form-group">
                <label className="vfl-form-label">External Form URL *</label>
                <Input
                  placeholder="https://..."
                  value={externalFormUrl}
                  onChange={(e) =>
                    dispatch({
                      type: "setField",
                      key: "externalFormUrl",
                      value: e.target.value
                    })
                  }
                />
              </div>
            )}

            {formType === "internal" && (
              <Button
                onClick={() => {
                  dispatch({ type: "setEditFieldIndex", payload: null });
                  dispatch({ type: "setEditorOpen", payload: true });
                }}
              >
                + Add Field
              </Button>
            )}

            {/* FIX #7: Template Name moved outside the internal-only block so
                it is available for all form types. Previously, external forms
                had no way to set a custom template name when saving as template. */}
            <div className="vfl-form-group">
              <label className="vfl-form-label">
                Template Name <span className="vfl-form-label-hint">(optional — only used if saving as template)</span>
              </label>
              <Input
                placeholder="Defaults to form title if left blank"
                value={templateName}
                onChange={(e) =>
                  dispatch({
                    type: "setField",
                    key: "templateName",
                    value: e.target.value
                  })
                }
              />
            </div>
          </div>
        </Card>

        {/* ── Field Builder — internal only ── */}
        {formType === "internal" && (
          <>
            <Card className="vfl-form-card">
              <h3 className="vfl-form-section-title">Fields</h3>
              <Divider style={{ margin: "12px 0" }} />
              <FieldList
                fields={fields}
                setFields={(f) => dispatch({ type: "setFields", payload: f })}
                setEditFieldIndex={(i) => dispatch({ type: "setEditFieldIndex", payload: i })}
                setEditorOpen={(v) => dispatch({ type: "setEditorOpen", payload: v })}
              />
            </Card>

            <Card className="vfl-form-card">
              <h3 className="vfl-form-section-title">Preview</h3>
              <Divider style={{ margin: "12px 0" }} />
              <FormPreview fields={fields} />
            </Card>
          </>
        )}

        {/* ── Save ── */}
        <div className="vfl-form-actions">
          <Button className="vfl-form-cancel-btn" onClick={() => navigate("/volunteering/formList")} disabled={isSaving}>
            Cancel
          </Button>

          {formType === "internal" ? (
            <Dropdown placement="topRight" menu={saveMenuItems}>
              <Button className="vfl-form-save-btn" loading={isSaving}>
                Save as...
              </Button>
            </Dropdown>
          ) : (
            <Dropdown placement="topRight" menu={saveMenuItems}>
              <Button className="vfl-form-save-btn" loading={isSaving}>
                Save as...
              </Button>
            </Dropdown>
          )}
        </div>
      </div>

      {/* ── Field Editor Modal ── */}
      {editorOpen && (
        <FieldEditor
          close={() => dispatch({ type: "setEditorOpen", payload: false })}
          fields={fields}
          setFields={(f) => dispatch({ type: "setFields", payload: f })}
          editIndex={editFieldIndex}
        />
      )}

      {/* ── Confirm Save Modal ── */}
      <ConfirmModal
        open={showConfirmModal}
        action="approve"
        entity="form"
        customTitle={
          state.saveIntent === "both" ? "Save as Form and Template" : state.saveIntent === "template" ? "Save as Template" : "Save as Form"
        }
        customMessage={
          state.saveIntent === "both"
            ? "This will create a live form and also save it as a reusable template."
            : state.saveIntent === "template"
              ? "This will save your form as a reusable template only. It won't appear as a live form."
              : "This will create a live form."
        }
        customButtonText="Save"
        loading={isSaving}
        onConfirm={handleSaveForm}
        onCancel={() => dispatch({ type: "setConfirmModal", payload: false })}
      />
    </Layout>
  );
}

export default CreateForm;
